import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CANT_VERIFY_REASONS, type CantVerifyReason } from "@/lib/cant-verify-reasons";

/**
 * The reason picker behind Can't verify (POS2-1083).
 *
 * One click per reason — choosing submits, with no second confirmation. The action
 * exists to get a labeller past an item they cannot use, so a two-step flow would
 * charge them for the camera's fault.
 *
 * The description says what the flag does *not* do, because the fear it has to
 * displace is Discard: labellers reach for that today, and it removes a real waste
 * event from a customer's reporting.
 */
export function CantVerifyDialog({
  open,
  isSubmitting,
  onClose,
  onChoose,
}: {
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onChoose: (reason: CantVerifyReason) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>What is wrong with this item?</DialogTitle>
          <DialogDescription>
            The event keeps its label, its cost and its place in the customer's data. This records
            that the capture could not be judged, and moves you on.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-1.5">
          {CANT_VERIFY_REASONS.map((reason) => (
            <button
              key={reason.id}
              type="button"
              disabled={isSubmitting}
              onClick={() => onChoose(reason.id)}
              className="rounded-md border border-gray-200 px-3.5 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="block text-sm font-medium text-gray-900">{reason.label}</span>
              <span className="mt-0.5 block text-xs text-gray-400">{reason.hint}</span>
            </button>
          ))}
        </div>

        <div className="flex justify-end pt-1">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
